import { useState } from "react";
import { formatDateTime as formatDate } from "@/lib/format-utils";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/query-client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2, AlertTriangle, CheckCircle, Info, Scale, Building2, FileCheck, Clock, History, Edit2, Lightbulb, Save } from "lucide-react";
import type { Lead } from "@shared/schema";
import { PERIL_TYPES } from "@/lib/enum-utils";
import { LeadAIFeedbackBar } from "@/components/leads/lead-ai-feedback-bar";

const US_STATES = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

// PERIL_TYPES imported from @/lib/enum-utils

const intakeFormSchema = z.object({
  state: z.string().min(2).max(2),
  city: z.string().min(1, "City is required"),
  lossAmount: z.string().min(1, "Loss amount is required").refine(
    (val) => !val || /^\d*\.?\d*$/.test(val),
    { message: "Loss amount must be a valid number" }
  ).refine(
    (val) => !val || parseFloat(val) >= 0,
    { message: "Loss amount cannot be negative" }
  ),
  isCommercialOrLargeResidential: z.boolean(),
  mplcLicensedInState: z.boolean(),
  coverageOpened: z.boolean(),
  valuationOnlyDispute: z.boolean(),
  contractorScopeMatchesCarrier: z.boolean(),
  roofShinglesIncludedInEstimate: z.boolean().optional(),
  negotiationStatus: z.enum(["early", "ongoing", "failed"]),
  peril: z.string().min(1, "Peril type is required"),
  dateOfLoss: z.string().optional(),
  notes: z.string().optional(),
});

type IntakeFormValues = z.infer<typeof intakeFormSchema>;

interface LeadIntakeDecisionFormProps {
  leadId: string;
  lead?: Lead;
  onSuccess?: () => void;
}

export function LeadIntakeDecisionForm({ leadId, lead, onSuccess }: LeadIntakeDecisionFormProps) {
  const { toast } = useToast();
  const [decisionResult, setDecisionResult] = useState<any>(null);

  const draft = lead?.intakeDraftData as IntakeFormValues | undefined;

  const form = useForm<IntakeFormValues>({
    resolver: zodResolver(intakeFormSchema),
    defaultValues: {
      state: draft?.state || lead?.propertyState || "",
      city: draft?.city || lead?.propertyCity || "",
      lossAmount: draft?.lossAmount || lead?.estimatedValue || "",
      isCommercialOrLargeResidential: draft?.isCommercialOrLargeResidential ?? !!lead?.company,
      mplcLicensedInState: draft?.mplcLicensedInState ?? true,
      coverageOpened: draft?.coverageOpened ?? false,
      valuationOnlyDispute: draft?.valuationOnlyDispute ?? false,
      contractorScopeMatchesCarrier: draft?.contractorScopeMatchesCarrier ?? false,
      roofShinglesIncludedInEstimate: draft?.roofShinglesIncludedInEstimate ?? undefined,
      negotiationStatus: draft?.negotiationStatus || "early",
      peril: draft?.peril || lead?.perilType || "",
      dateOfLoss: draft?.dateOfLoss || (lead?.dateOfLoss ? String(lead.dateOfLoss).substring(0, 10) : ""),
      notes: draft?.notes || "",
    },
  });

  const createDecisionMutation = useMutation({
    mutationFn: async (data: IntakeFormValues) => {
      const payload = {
        state: data.state,
        city: data.city,
        lossAmount: parseFloat(data.lossAmount),
        isCommercialOrLargeResidential: data.isCommercialOrLargeResidential,
        mplcLicensedInState: data.mplcLicensedInState,
        coverageOpened: data.coverageOpened,
        valuationOnlyDispute: data.valuationOnlyDispute,
        contractorScopeMatchesCarrier: data.contractorScopeMatchesCarrier,
        roofShinglesIncludedInEstimate: data.roofShinglesIncludedInEstimate,
        negotiationStatus: data.negotiationStatus,
        peril: data.peril,
        dateOfLoss: data.dateOfLoss || undefined,
        notes: data.notes,
      };

      const res = await apiRequest("POST", `/api/leads/${leadId}/intake-decision`, payload);
      return await res.json();
    },
    onSuccess: (data) => {
      setDecisionResult(data);
      queryClient.invalidateQueries({ queryKey: ["/api/intake-decisions/lead", leadId] });
      queryClient.invalidateQueries({ queryKey: ["/api/leads", leadId] });
      toast({
        title: "Decision Created",
        description: "Intake decision has been successfully created for this lead.",
      });
      onSuccess?.();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to evaluate intake decision",
        variant: "destructive",
      });
    },
  });

  const saveDraftMutation = useMutation({
    mutationFn: async (data: IntakeFormValues) => {
      const res = await apiRequest("PATCH", `/api/leads/${leadId}`, { intakeDraftData: data });
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/leads", leadId] });
      toast({
        title: "Saved",
        description: "Intake form data has been saved.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save intake form data",
        variant: "destructive",
      });
    },
  });

  const onSaveDraft = (data: IntakeFormValues) => {
    saveDraftMutation.mutate(data);
  };

  const onSubmit = (data: IntakeFormValues) => {
    createDecisionMutation.mutate(data);
  };

  const getServiceIcon = (service: string) => {
    switch (service) {
      case "public_adjuster":
        return <Building2 className="h-5 w-5" />;
      case "appraisal":
        return <Scale className="h-5 w-5" />;
      case "both":
        return <FileCheck className="h-5 w-5" />;
      default:
        return <AlertTriangle className="h-5 w-5" />;
    }
  };

  const getServiceColor = (service: string) => {
    switch (service) {
      case "public_adjuster":
        return "text-blue-600 dark:text-blue-400";
      case "appraisal":
        return "text-green-600 dark:text-green-400";
      case "both":
        return "text-purple-600 dark:text-purple-400";
      default:
        return "text-red-600 dark:text-red-400";
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-primary" />
            <CardTitle>Evaluate Lead for Intake</CardTitle>
          </div>
          <CardDescription>
            Determine whether this lead should be taken on as a PA file or Appraisal file.
            This evaluation helps decide the best path forward before converting to a claim.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSaveDraft)} className="space-y-6">
              {/* Geographic Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Geographic & Licensing</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>City</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="e.g., St. Louis" data-testid="input-lead-intake-city" />
                        </FormControl>
                        <FormDescription>
                          Certain cities have lower PA minimums ($100k vs $500k)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="state"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>State</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value} data-testid="select-lead-intake-state">
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select state" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent className="max-h-60">
                            {US_STATES.map((state) => (
                              <SelectItem key={state.value} value={state.value} data-testid={`lead-state-${state.value}`}>
                                {state.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="mplcLicensedInState"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                      <div className="space-y-0.5">
                        <FormLabel>MPLC Licensed in State</FormLabel>
                        <FormDescription>
                          Does MPLC have required licenses in this state?
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          data-testid="switch-lead-mplc-licensed"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              {/* Financial Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Financial Information</h3>
                <FormField
                  control={form.control}
                  name="lossAmount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Estimated Loss Amount</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="e.g., 250000"
                          data-testid="input-lead-loss-amount"
                          onKeyDown={(e) => {
                            // Allow: backspace, delete, tab, escape, enter, .
                            if ([46, 8, 9, 27, 13, 110, 190].indexOf(e.keyCode) !== -1 ||
                                // Allow: Ctrl+A, Command+A
                                (e.keyCode === 65 && (e.ctrlKey === true || e.metaKey === true)) ||
                                // Allow: home, end, left, right
                                (e.keyCode >= 35 && e.keyCode <= 40)) {
                                     // let it happen, don't do anything
                                     return;
                            }
                            // Ensure that it is a number and stop the keypress
                            if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) {
                                e.preventDefault();
                            }
                          }}
                        />
                      </FormControl>
                      <FormDescription>
                        PA minimums: $100k (select cities) or $500k (default)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Claim Characteristics */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Lead Characteristics</h3>

                <FormField
                  control={form.control}
                  name="peril"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Peril Type</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value} data-testid="select-lead-intake-peril">
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select peril type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {PERIL_TYPES.map((peril) => (
                            <SelectItem key={peril.value} value={peril.value} data-testid={`lead-peril-${peril.value}`}>
                              {peril.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="isCommercialOrLargeResidential"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                      <div className="space-y-0.5">
                        <FormLabel>Commercial or Large Residential</FormLabel>
                        <FormDescription>
                          Is this a commercial property or large residential claim?
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          data-testid="switch-lead-commercial"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="dateOfLoss"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date of Loss (Optional)</FormLabel>
                      <FormControl>
                        <Input {...field} type="date" max={new Date().toISOString().split('T')[0]} data-testid="input-lead-date-of-loss" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Coverage & Scope */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Coverage & Scope Analysis</h3>

                <FormField
                  control={form.control}
                  name="coverageOpened"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                      <div className="space-y-0.5">
                        <FormLabel>Coverage Opened</FormLabel>
                        <FormDescription>
                          Has carrier acknowledged coverage for disputed items?
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          data-testid="switch-lead-coverage-opened"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="valuationOnlyDispute"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                      <div className="space-y-0.5">
                        <FormLabel>Valuation-Only Dispute</FormLabel>
                        <FormDescription>
                          Both sides agree on coverage, only price/quantity differs?
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          data-testid="switch-lead-valuation-only"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="contractorScopeMatchesCarrier"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                      <div className="space-y-0.5">
                        <FormLabel>Contractor Scope Matches Carrier</FormLabel>
                        <FormDescription>
                          Same scope/quantities, different pricing? (Scope-match pathway)
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          data-testid="switch-lead-scope-match"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="roofShinglesIncludedInEstimate"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                      <div className="space-y-0.5">
                        <FormLabel>Roof Shingles in Estimate (Optional)</FormLabel>
                        <FormDescription>
                          For roof claims: Are shingles included in estimate?
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value ?? false}
                          onCheckedChange={field.onChange}
                          data-testid="switch-lead-roof-shingles"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>

              {/* Negotiation Context */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Negotiation Context</h3>

                <FormField
                  control={form.control}
                  name="negotiationStatus"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Negotiation Status</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value} data-testid="select-lead-negotiation-status">
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="early" data-testid="lead-negotiation-early">Early Stage</SelectItem>
                          <SelectItem value="ongoing" data-testid="lead-negotiation-ongoing">Ongoing</SelectItem>
                          <SelectItem value="failed" data-testid="lead-negotiation-failed">Failed/Stalled</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Current status of settlement negotiations (if any)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Additional Notes */}
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Intake decision notes (optional)</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Any additional context or considerations..."
                        className="min-h-[100px]"
                        data-testid="textarea-lead-intake-notes"
                      />
                    </FormControl>
                    <FormDescription>
                      Saved with this intake decision. Lead notes are on the Notes tab.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex flex-col gap-2">
                <Button
                  type="button"
                  className="w-full"
                  disabled={saveDraftMutation.isPending}
                  onClick={form.handleSubmit(onSaveDraft)}
                  data-testid="button-save-intake-draft"
                >
                  {saveDraftMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Save
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  disabled={createDecisionMutation.isPending}
                  onClick={form.handleSubmit(onSubmit)}
                  data-testid="button-create-lead-decision"
                >
                  {createDecisionMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Evaluating...
                    </>
                  ) : (
                    <>
                      <Lightbulb className="mr-2 h-4 w-4" />
                      Evaluate Intake Decision
                    </>
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      {/* Decision Result Display */}
      {decisionResult && (
        <Card className="border-primary">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-primary" />
              Intake Evaluation Result
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert className={decisionResult.requiresManualReview ? "border-yellow-600" : ""}>
              <div className={`flex items-center gap-2 ${getServiceColor(decisionResult.recommendedService)}`}>
                {getServiceIcon(decisionResult.recommendedService)}
                <AlertTitle className="mb-0">
                  Recommended: {decisionResult.recommendedService?.replace(/_/g, " ").toUpperCase()}
                </AlertTitle>
              </div>
              <AlertDescription className="mt-2">
                <p className="font-medium">Pathway: {decisionResult.decisionPathway?.replace(/_/g, " ")}</p>
                <p className="mt-2 text-sm">{decisionResult.decisionRationale}</p>
              </AlertDescription>
            </Alert>

            {decisionResult.requiresManualReview && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Manual Review Required</AlertTitle>
                <AlertDescription>
                  This decision requires manual review due to complexity or conflicting data.
                </AlertDescription>
              </Alert>
            )}

            {decisionResult.jurisdictionalNotes && (
              <div className="rounded-lg bg-muted p-4">
                <div className="flex items-start gap-2">
                  <Info className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <h4 className="font-semibold mb-1">Jurisdictional Notes</h4>
                    <p className="text-sm text-muted-foreground">{decisionResult.jurisdictionalNotes}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className={`rounded-lg border p-3 ${decisionResult.paAllowed ? "bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800" : "bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800"}`}>
                <p className="font-semibold text-sm">Public Adjuster</p>
                <p className={`text-xs ${decisionResult.paAllowed ? "text-green-700 dark:text-green-400" : "text-red-700 dark:text-red-400"}`}>
                  {decisionResult.paAllowed ? "Available" : "Not Available"}
                </p>
              </div>
              <div className={`rounded-lg border p-3 ${decisionResult.appraisalAllowed ? "bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800" : "bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800"}`}>
                <p className="font-semibold text-sm">Appraisal</p>
                <p className={`text-xs ${decisionResult.appraisalAllowed ? "text-green-700 dark:text-green-400" : "text-red-700 dark:text-red-400"}`}>
                  {decisionResult.appraisalAllowed ? "Available" : "Not Available"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

interface LeadIntakeDecisionHistoryProps {
  leadId: string;
}

export function LeadIntakeDecisionHistory({ leadId }: LeadIntakeDecisionHistoryProps) {
  const { toast } = useToast();
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [selectedDecision, setSelectedDecision] = useState<any>(null);
  const [overrideReason, setOverrideReason] = useState("");
  const [overrideService, setOverrideService] = useState("");

  const { data: decisions = [], isLoading } = useQuery<any[]>({
    queryKey: ["/api/intake-decisions/lead", leadId],
  });

  const overrideMutation = useMutation({
    mutationFn: async (data: { decisionId: string; overrideReason: string; recommendedService: string }) => {
      return await apiRequest("PATCH", `/api/intake-decisions/${data.decisionId}/override`, {
        decisionOverridden: true,
        overrideReason: data.overrideReason,
        recommendedService: data.recommendedService,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/intake-decisions/lead", leadId] });
      queryClient.invalidateQueries({ queryKey: ["/api/leads", leadId] });
      toast({
        title: "Decision Overridden",
        description: "Intake decision has been successfully overridden.",
      });
      setShowOverrideModal(false);
      setOverrideReason("");
      setOverrideService("");
      setSelectedDecision(null);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to override decision",
        variant: "destructive",
      });
    },
  });

  const handleOverride = () => {
    if (!selectedDecision || !overrideReason || !overrideService) {
      toast({
        title: "Missing Information",
        description: "Please provide both override reason and service selection",
        variant: "destructive",
      });
      return;
    }

    overrideMutation.mutate({
      decisionId: selectedDecision.id,
      overrideReason,
      recommendedService: overrideService,
    });
  };

  const getServiceIcon = (service: string) => {
    switch (service) {
      case "public_adjuster":
        return <Building2 className="h-5 w-5" />;
      case "appraisal":
        return <Scale className="h-5 w-5" />;
      case "both":
        return <FileCheck className="h-5 w-5" />;
      default:
        return <AlertTriangle className="h-5 w-5" />;
    }
  };

  const getServiceColor = (service: string) => {
    switch (service) {
      case "public_adjuster":
        return "text-blue-600 dark:text-blue-400";
      case "appraisal":
        return "text-green-600 dark:text-green-400";
      case "both":
        return "text-purple-600 dark:text-purple-400";
      default:
        return "text-red-600 dark:text-red-400";
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!decisions || decisions.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Intake Decision History
          </CardTitle>
          <CardDescription>No intake decisions have been evaluated for this lead yet</CardDescription>
        </CardHeader>
      </Card>
    );
  }



  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Intake Decision History
                <Badge variant="outline" className="ml-2">
                  {decisions.length} {decisions.length === 1 ? 'Decision' : 'Decisions'}
                </Badge>
              </CardTitle>
              <CardDescription>
                {decisions.length > 1 ? 'All decisions for this lead' : 'Current decision for this lead'}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {decisions.map((decision, index) => (
            <div
              key={decision.id}
              className={`space-y-4 ${index === 0 ? 'border-2 border-primary rounded-lg p-4' : 'border-l-2 border-muted pl-4'}`}
              data-testid={`lead-decision-${decision.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <span className="font-semibold">
                    {index === 0 ? 'Current Decision' : `Previous Decision #${decisions.length - index}`}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {formatDate(decision.createdAt)}
                  </span>
                  {decision.evaluatedBy === 'system-ai' && (
                    <Badge variant="secondary" className="ml-2" data-testid={`badge-lead-automated-${decision.id}`}>
                      <span className="flex items-center gap-1">
                        <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        Automated
                      </span>
                    </Badge>
                  )}
                </div>
                {index === 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setSelectedDecision(decision);
                      setOverrideService(decision.recommendedService);
                      setShowOverrideModal(true);
                    }}
                    data-testid="button-override-lead-decision"
                  >
                    <Edit2 className="h-4 w-4 mr-2" />
                    Override
                  </Button>
                )}
              </div>

              <Alert className={decision.requiresManualReview ? "border-yellow-600" : ""}>
                <div className={`flex items-center gap-2 ${getServiceColor(decision.recommendedService)}`}>
                  {getServiceIcon(decision.recommendedService)}
                  <AlertTitle className="mb-0">
                    Recommended: {decision.recommendedService?.replace(/_/g, " ").toUpperCase()}
                  </AlertTitle>
                </div>
                <AlertDescription className="mt-2">
                  <p className="font-medium">Pathway: {decision.decisionPathway?.replace(/_/g, " ")}</p>
                  <p className="mt-2 text-sm">{decision.decisionRationale}</p>
                </AlertDescription>
              </Alert>

              {decision.decisionOverridden && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Decision Overridden</AlertTitle>
                  <AlertDescription>
                    <p className="mt-1">
                      <strong>Reason:</strong> {decision.overrideReason}
                    </p>
                    {decision.reviewedBy && decision.reviewedAt && (
                      <p className="text-xs mt-1">
                        Reviewed by: {decision.reviewedBy} on {formatDate(decision.reviewedAt)}
                      </p>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              {decision.requiresManualReview && !decision.decisionOverridden && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Manual Review Required</AlertTitle>
                  <AlertDescription>
                    This decision requires manual review due to complexity or conflicting data.
                  </AlertDescription>
                </Alert>
              )}

              {decision.jurisdictionalNotes && (
                <div className="rounded-lg bg-muted p-4">
                  <div className="flex items-start gap-2">
                    <Info className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <h4 className="font-semibold mb-1">Jurisdictional Notes</h4>
                      <p className="text-sm text-muted-foreground">{decision.jurisdictionalNotes}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Provide AI Feedback for automated decisions */}
              {decision.evaluatedBy === 'system-ai' && (
                <div className="pt-2 border-t">
                  <LeadAIFeedbackBar
                    leadId={leadId}
                    feature="intake_recommendation"
                    interactionId={(decision as any).interactionId || leadId}
                    originalResult={decision}
                    title="Intake Recommendation"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div
                  className={`rounded-lg border p-3 ${
                    decision.paAllowed
                      ? "bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800"
                      : "bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800"
                  }`}
                >
                  <p className="font-semibold text-sm">Public Adjuster</p>
                  <p
                    className={`text-xs ${
                      decision.paAllowed ? "text-green-700 dark:text-green-400" : "text-red-700 dark:text-red-400"
                    }`}
                  >
                    {decision.paAllowed ? "Available" : "Not Available"}
                  </p>
                </div>
                <div
                  className={`rounded-lg border p-3 ${
                    decision.appraisalAllowed
                      ? "bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800"
                      : "bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800"
                  }`}
                >
                  <p className="font-semibold text-sm">Appraisal</p>
                  <p
                    className={`text-xs ${
                      decision.appraisalAllowed
                        ? "text-green-700 dark:text-green-400"
                        : "text-red-700 dark:text-red-400"
                    }`}
                  >
                    {decision.appraisalAllowed ? "Available" : "Not Available"}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Location</Label>
                  <p className="text-sm font-medium" data-testid="text-lead-decision-location">
                    {decision.city}, {decision.state}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Loss Amount</Label>
                  <p className="text-sm font-medium" data-testid="text-lead-decision-loss-amount">
                    ${parseFloat(decision.lossAmount).toLocaleString()}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">PA Threshold</Label>
                  <p className="text-sm font-medium" data-testid="text-lead-pa-threshold">
                    ${parseFloat(decision.paMinimumThreshold).toLocaleString()}
                  </p>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Negotiation Status</Label>
                  <Badge variant="outline" data-testid="badge-lead-negotiation-status">
                    {decision.negotiationStatus}
                  </Badge>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Dialog open={showOverrideModal} onOpenChange={setShowOverrideModal}>
        <DialogContent data-testid="dialog-override-lead-decision">
          <DialogHeader>
            <DialogTitle>Override Intake Decision</DialogTitle>
            <DialogDescription>
              Provide a reason for overriding the decision and select the correct service
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="lead-override-service">Corrected Service Recommendation</Label>
              <Select value={overrideService} onValueChange={setOverrideService} data-testid="select-lead-override-service">
                <SelectTrigger id="lead-override-service">
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public_adjuster" data-testid="lead-service-public-adjuster">Public Adjuster</SelectItem>
                  <SelectItem value="appraisal" data-testid="lead-service-appraisal">Appraisal</SelectItem>
                  <SelectItem value="both" data-testid="lead-service-both">Both Services</SelectItem>
                  <SelectItem value="none" data-testid="lead-service-none">Neither Service</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="lead-override-reason">Override Reason</Label>
              <Textarea
                id="lead-override-reason"
                placeholder="Explain why this decision should be overridden..."
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                className="min-h-[100px]"
                data-testid="textarea-lead-override-reason"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowOverrideModal(false);
                setOverrideReason("");
                setOverrideService("");
                setSelectedDecision(null);
              }}
              data-testid="button-cancel-lead-override"
            >
              Cancel
            </Button>
            <Button
              onClick={handleOverride}
              disabled={overrideMutation.isPending || !overrideReason || !overrideService}
              data-testid="button-confirm-lead-override"
            >
              {overrideMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Overriding...
                </>
              ) : (
                "Override Decision"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
